import React, { useState, useRef, useEffect } from 'react'
import { TimeEntry, WeeklyEntries, ViewMode } from '../../types'
import { timeUtils } from '../../services/timerService'
import { Clock, Play, Square, Edit, Trash2, PlusCircle, MoreVertical, StopCircle } from 'lucide-react'
import ViewToggle from './ViewToggle'
import WeeklyTimeEntryList from './WeeklyTimeEntryList'

interface TimeEntryListProps {
  entries: TimeEntry[]
  totalDuration: number
  weeklyEntries?: WeeklyEntries
  weeklyTotalDuration?: number
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  onEdit?: (entry: TimeEntry) => void
  onDelete?: (entryId: string) => void
  onStop?: () => void
  onManualAdd?: () => void
  className?: string
  disabled?: boolean
}

const TimeEntryList: React.FC<TimeEntryListProps> = ({
  entries,
  totalDuration,
  weeklyEntries,
  weeklyTotalDuration,
  viewMode,
  onViewModeChange,
  onEdit,
  onDelete,
  onStop,
  onManualAdd,
  className = '',
  disabled = false
}) => {
  const currentTotalDuration = viewMode === 'today' ? totalDuration : (weeklyTotalDuration || 0)
  const totalLabel = viewMode === 'today' ? '今日總計' : '本週總計'

  return (
    <div className={`${className}`}>
      {/* 標題行 */}
      <div className="flex items-center justify-between mb-4">
        {/* 左側：標題和檢視切換 */}
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-primary-600" />
            記錄
          </h3>
          
          {/* 檢視模式切換 */}
          <ViewToggle 
            currentView={viewMode}
            onViewChange={onViewModeChange}
          />
          
          {/* 桌機版：手動新增按鈕 */}
          {onManualAdd && (
            <button
              onClick={disabled ? undefined : onManualAdd}
              disabled={disabled}
              className={`hidden md:flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                disabled 
                  ? 'bg-secondary-300 text-secondary-500 cursor-not-allowed' 
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
              title={disabled ? '此操作已被鎖定' : '手動新增時間記錄'}
            >
              <PlusCircle className="w-4 h-4 mr-1" />
              手動新增
            </button>
          )}
        </div>
        
        {/* 右側：手動新增按鈕（手機版）和總計 */}
        <div className="flex items-center space-x-3">
          {/* 手機版：手動新增按鈕 */}
          {onManualAdd && (
            <button
              onClick={disabled ? undefined : onManualAdd}
              disabled={disabled}
              className={`md:hidden flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                disabled 
                  ? 'bg-secondary-300 text-secondary-500 cursor-not-allowed' 
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
              title={disabled ? '此操作已被鎖定' : '手動新增時間記錄'}
            >
              <PlusCircle className="w-4 h-4 mr-1" />
              手動新增
            </button>
          )}
          
          {/* 桌機版：總計顯示 */}
          <div className="text-sm text-secondary-600 hidden md:block">
            {totalLabel}: <span className="font-semibold text-primary-600">
              {timeUtils.formatDurationHuman(currentTotalDuration)}
            </span>
          </div>
        </div>
      </div>

      {/* 記錄內容 */}
      <div className="space-y-3">
        {viewMode === 'today' ? (
          // 今日檢視
          entries.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 text-secondary-300 mx-auto mb-3" />
              <p className="text-secondary-500">今日尚無計時記錄</p>
              <p className="text-sm text-secondary-400 mt-1">
                選擇專案並開始計時來建立第一筆記錄
              </p>
            </div>
          ) : (
            entries.map((entry) => (
              <TimeEntryItem
                key={entry.id}
                entry={entry}
                onEdit={onEdit}
                onDelete={onDelete}
                onStop={onStop}
                disabled={disabled}
              />
            ))
          )
        ) : (
          // 週檢視
          weeklyEntries ? (
            <WeeklyTimeEntryList
              weeklyEntries={weeklyEntries}
              onEdit={onEdit}
              onDelete={onDelete}
              onStop={onStop}
              disabled={disabled}
            />
          ) : (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 text-secondary-300 mx-auto mb-3" />
              <p className="text-secondary-500">載入週記錄中...</p>
            </div>
          )
        )}
      </div>
    </div>
  )
}

// 更多操作選單組件
interface MoreActionsDropdownProps {
  entry: TimeEntry
  onEdit?: (entry: TimeEntry) => void
  onDelete?: (entryId: string) => void
  onStop?: () => void
  disabled?: boolean
}

const MoreActionsDropdown: React.FC<MoreActionsDropdownProps> = ({
  entry,
  onEdit,
  onDelete,
  onStop,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isRunning = entry.status === 'RUNNING'

  // 點擊外部關閉選單
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleAction = (action: () => void) => {
    action()
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          p-2 transition-colors rounded-lg
          ${disabled 
            ? 'text-secondary-300 cursor-not-allowed' 
            : 'text-secondary-400 hover:text-secondary-600 hover:bg-secondary-50'
          }
        `}
        title="更多操作"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {isOpen && !disabled && (
        <div className="absolute right-0 top-8 mt-1 w-32 bg-white rounded-lg shadow-lg border border-secondary-200 py-1 z-10">
          {isRunning ? (
            onStop && (
              <button
                onClick={() => handleAction(onStop)}
                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
              >
                <StopCircle className="w-4 h-4 mr-2" />
                停止計時
              </button>
            )
          ) : (
            <>
              {onEdit && (
                <button
                  onClick={() => handleAction(() => onEdit(entry))}
                  className="w-full text-left px-3 py-2 text-sm text-secondary-700 hover:bg-secondary-50 flex items-center"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  編輯記錄
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => handleAction(() => onDelete(entry.id))}
                  className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  刪除記錄
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// 單筆計時記錄組件
interface TimeEntryItemProps {
  entry: TimeEntry
  onEdit?: (entry: TimeEntry) => void
  onDelete?: (entryId: string) => void
  onStop?: () => void
  disabled?: boolean
}

const TimeEntryItem: React.FC<TimeEntryItemProps> = ({
  entry,
  onEdit,
  onDelete,
  onStop,
  disabled = false
}) => {
  const isRunning = entry.status === 'RUNNING'
  const duration = isRunning 
    ? timeUtils.calculateDuration(entry.startTime)
    : entry.duration

  return (
    <div className={`
      group p-4 bg-white border rounded-lg shadow-sm
      ${isRunning ? 'border-green-200 bg-green-50' : 'border-secondary-200'}
      transition-all duration-200 hover:shadow-md
    `}>
      {/* 手機版佈局 */}
      <div className="block lg:hidden">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div 
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: entry.project.color }}
            />
            <span className="font-medium text-secondary-800">
              {entry.project.name}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`
              text-lg font-mono font-semibold
              ${isRunning ? 'text-green-600' : 'text-secondary-600'}
            `}>
              {timeUtils.formatDuration(duration)}
            </div>
            <MoreActionsDropdown
              entry={entry}
              onEdit={onEdit}
              onDelete={onDelete}
              onStop={onStop}
              disabled={disabled}
            />
          </div>
        </div>
        
        <div className="flex items-center space-x-2 mb-2">
          <span className="text-sm text-secondary-500">
            {timeUtils.formatTime(entry.startTime)}
          </span>
          <span className="text-secondary-300">-</span>
          {entry.endTime ? (
            <span className="text-sm text-secondary-500">
              {timeUtils.formatTime(entry.endTime)}
            </span>
          ) : (
            <span className="text-sm text-green-600 font-medium flex items-center">
              進行中
              <div className="w-2 h-2 bg-green-500 rounded-full ml-1 animate-pulse"></div>
            </span>
          )}
          
          {/* 狀態標籤 */}
          <div className="flex items-center space-x-1">
            {entry.isManual && (
              <div className="flex items-center bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
                <PlusCircle className="w-3 h-3 mr-1" />
                <span>手動</span>
              </div>
            )}
            {entry.isEdited && (
              <div className="flex items-center bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                <Edit className="w-3 h-3 mr-1" />
                <span>已編輯</span>
              </div>
            )}
          </div>
        </div>
        
        {entry.description && (
          <p className="text-sm text-secondary-600 mt-2">
            {entry.description}
          </p>
        )}
      </div>

      {/* 桌機版佈局 */}
      <div className="hidden lg:block">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.project.color }}
              />
              <span className="font-medium text-secondary-800">
                {entry.project.name}
              </span>
              
              {/* 狀態指示器 */}
              <div className="flex items-center space-x-1">
                {isRunning ? (
                  <>
                    <Play className="w-3 h-3 text-green-600" />
                    <span className="text-xs text-green-600 font-medium">進行中</span>
                  </>
                ) : (
                  <>
                    <Square className="w-3 h-3 text-secondary-500" />
                    <span className="text-xs text-secondary-500">已完成</span>
                  </>
                )}
              </div>

              {/* 標籤區域 */}
              <div className="flex items-center space-x-2">
                {entry.isManual && (
                  <div 
                    className="flex items-center bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium"
                    title={entry.manualReason ? `手動新增原因：${entry.manualReason}` : '手動新增記錄'}
                  >
                    <PlusCircle className="w-3 h-3 mr-1" />
                    <span>手動新增</span>
                  </div>
                )}
                {entry.isEdited && (
                  <div className="flex items-center bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                    <Edit className="w-3 h-3 mr-1" />
                    <span>已編輯</span>
                  </div>
                )}
              </div>
            </div>

            {entry.description && (
              <p className="text-sm text-secondary-600 mb-2">
                {entry.description}
              </p>
            )}

            <div className="flex items-center space-x-4 text-xs text-secondary-500">
              <span>
                開始: {timeUtils.formatTime(entry.startTime)}
              </span>
              {entry.endTime && (
                <span>
                  結束: {timeUtils.formatTime(entry.endTime)}
                </span>
              )}
              <span className="font-medium">
                時長: {timeUtils.formatDurationHuman(duration)}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-2 ml-4">
            <div className={`
              text-lg font-mono font-semibold
              ${isRunning ? 'text-green-600' : 'text-secondary-600'}
            `}>
              {timeUtils.formatDuration(duration)}
            </div>

            {/* 桌機版：預設顯示三個點，懸停顯示操作按鈕 */}
            <div className="flex items-center">
              {/* 懸停顯示的操作按鈕 */}
              {!isRunning && (
                <div className="hidden group-hover:flex items-center space-x-1 mr-2">
                  {onEdit && (
                    <button
                      onClick={disabled ? undefined : () => onEdit(entry)}
                      disabled={disabled}
                      className={`p-2 transition-colors rounded-lg ${
                        disabled 
                          ? 'text-secondary-300 cursor-not-allowed' 
                          : 'text-secondary-400 hover:text-primary-600 hover:bg-primary-50'
                      }`}
                      title={disabled ? '此操作已被鎖定' : '編輯記錄'}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  
                  {onDelete && (
                    <button
                      onClick={disabled ? undefined : () => onDelete(entry.id)}
                      disabled={disabled}
                      className={`p-2 transition-colors rounded-lg ${
                        disabled 
                          ? 'text-secondary-300 cursor-not-allowed' 
                          : 'text-secondary-400 hover:text-red-600 hover:bg-red-50'
                      }`}
                      title={disabled ? '此操作已被鎖定' : '刪除記錄'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}

              {/* 三個點選單：預設顯示，懸停時隱藏 */}
              <div className="group-hover:hidden">
                <MoreActionsDropdown
                  entry={entry}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onStop={onStop}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TimeEntryList
